/**
 * "O que só você decide" (docs/refactor/18-configuracoes-sensiveis.md): a short step, not a settings
 * screen, that gathers the switches that are the person's own call, at the moments they matter — a
 * project created or linked, someone invited, an invitation accepted. Each switch is the same one that
 * lives in its own place (Comandos de fora, Conta), the AI memory among them (docs/refactor/17); "Pular" always.
 *
 * Opened from anywhere with askSensitive(); drawn once, in the Shell.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { Modal } from './Modal'
import { ConfirmIdentity } from './ConfirmIdentity'
import { useTeamWorkToggle } from '../lib/teamWorkToggle'
import { MemorySwitches } from './MemorySwitches'

export const SENSITIVE_EVENT = 'devpulse:sensitive-step'

/** Opens the step (for the person signed in). `reason` says why now: "project", "invite", "joined". */
export function askSensitive(reason: 'project' | 'invite' | 'joined'): void {
  window.dispatchEvent(new CustomEvent(SENSITIVE_EVENT, { detail: reason }))
}

const DAYS = [1, 7, 30] as const

export function SensitiveStep() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState<string | null>(null)
  const [days, setDays] = useState<(typeof DAYS)[number]>(7)
  const [confirming, setConfirming] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    // Someone who just accepted an invitation lands here after a full page load: the address says so.
    const params = new URLSearchParams(window.location.search)
    if (params.get('sensitive') === 'joined') {
      setReason('joined')
      params.delete('sensitive')
      const rest = params.toString()
      window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`)
    }
  }, [])

  useEffect(() => {
    const open = (event: Event) => setReason(String((event as CustomEvent<string>).detail ?? 'project'))
    window.addEventListener(SENSITIVE_EVENT, open)
    return () => window.removeEventListener(SENSITIVE_EVENT, open)
  }, [])

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000, enabled: reason !== null })
  const { data: remote } = useQuery({ queryKey: ['remote'], queryFn: api.remote.overview, enabled: reason !== null && Boolean(me?.developerId), retry: false })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['remote'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }
  const remoteOn = useMutation({
    mutationFn: () => api.remote.setEnabled(true, days * 24),
    onSuccess: () => { setConfirming(false); setProblem(null); refresh() },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'reauth_required') setConfirming(true)
      else setProblem(t('error.generic'))
    },
  })
  const remoteOff = useMutation({ mutationFn: () => api.remote.setEnabled(false), onSuccess: refresh })
  // Colegas mandam para mim (docs/refactor/63): off by default; switching on asks who you are, as everywhere it shows.
  const { set: team, dialog: teamConfirm } = useTeamWorkToggle()

  if (reason === null || !me?.developerId) return null
  const close = () => {
    setReason(null)
    setConfirming(false)
    setProblem(null)
  }
  const until = remote?.enabled ? remote.until ?? null : null

  return (
    <Modal title={t('sensitive.title')} onClose={close}>
      <div className="stack sensitive">
        <p className="small muted" style={{ margin: 0 }}>{t(`sensitive.why.${reason}`)}</p>

        <section className="sensitive__item">
          <strong className="small">{t('sensitive.remote.title')}</strong>
          <span className="tiny muted">{t('sensitive.remote.hint')}</span>
          {remote?.enabled ? (
            <div className="row row--tight row--wrap">
              <span className="tiny">{until ? t('sensitive.remote.onUntil', { when: formatRelative(until, locale) }) : t('sensitive.remote.on')}</span>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => remoteOff.mutate()} disabled={remoteOff.isPending}>{t('sensitive.remote.off')}</button>
            </div>
          ) : remote && !remote.allowed ? (
            <span className="tiny muted">{t('sensitive.remote.notAllowed')}</span>
          ) : (
            <div className="row row--tight row--wrap">
              {DAYS.map((d) => (
                <button key={d} type="button" className={`btn btn--sm${days === d ? ' btn--primary' : ' btn--ghost'}`} onClick={() => setDays(d)}>
                  {t(d === 1 ? 'remote.days.one' : 'remote.days', { count: d })}
                </button>
              ))}
              <button type="button" className="btn btn--sm" onClick={() => remoteOn.mutate()} disabled={remoteOn.isPending}>{t('sensitive.remote.turnOn')}</button>
            </div>
          )}
          {confirming && <ConfirmIdentity onConfirmed={() => remoteOn.mutate()} onCancel={() => setConfirming(false)} />}
        </section>

        <label className="sensitive__item row row--tight">
          <input type="checkbox" checked={Boolean(remote?.acceptTeamWork)} disabled={team.isPending || !remote?.allowed} onChange={(event) => team.mutate(event.target.checked)} />
          <span className="stack" style={{ gap: 2 }}>
            <strong className="small">{t('sensitive.team.title')}</strong>
            <span className="tiny muted">{t('sensitive.team.hint')}</span>
          </span>
        </label>
        {teamConfirm}

        {/* The AI memory, and sharing it with the project's AIs (docs/refactor/17). */}
        <MemorySwitches itemClassName="sensitive__item row row--tight" />

        {problem && <p className="tiny run-bar__notice--bad" style={{ margin: 0 }}>{problem}</p>}
        <div className="row row--tight">
          <span className="spacer" />
          <button type="button" className="btn btn--sm btn--ghost" onClick={close}>{t('sensitive.skip')}</button>
          <button type="button" className="btn btn--sm btn--primary" onClick={close}>{t('sensitive.done')}</button>
        </div>
      </div>
    </Modal>
  )
}
